/**
 * db — client main-thread del worker SQLite (protocollo richiesta/risposta con `id`).
 * API pubblica: initDb / appendEvents / listEvents. Gli errori arrivano come Error con NOME
 * stabile (HyprStorageUnavailable / HyprStorageWrite / nomi nativi preservati): l'UI li mappa
 * in italiano via i18n (regola 1), il messaggio grezzo è solo diagnosi tecnica.
 */
import type { HyprEvent } from "./events";

type Res =
  | { id: number; ok: true; data: unknown }
  | { id: number; ok: false; error: { name: string; message: string } };

interface Pending {
  resolve: (v: unknown) => void;
  reject: (e: Error) => void;
}

let worker: Worker | null = null;
let nextId = 1;
let initPromise: Promise<{ mode: string }> | null = null;
const pending = new Map<number, Pending>();

// regola 1: mai stato inesplicato. Se il worker non parte (blocco di piattaforma, errore di
// caricamento) initDb() deve FALLIRE con un errore mappabile in status line, non restare
// sospeso per sempre con i pulsanti disabilitati.
const INIT_TIMEOUT_MS = 15_000;

export function storageAvailable(): boolean {
  return typeof Worker !== "undefined" && typeof WebAssembly !== "undefined";
}

function storageError(name: string, message: string): Error {
  const e = new Error(message);
  e.name = name;
  return e;
}

function rejectAllPending(err: Error): void {
  for (const p of pending.values()) p.reject(err);
  pending.clear();
}

function ensureWorker(): Worker {
  if (worker) return worker;
  worker = new Worker(new URL("./db-worker.ts", import.meta.url), { type: "module" });
  worker.onmessage = (msg: MessageEvent<Res>) => {
    const p = pending.get(msg.data.id);
    if (!p) return;
    pending.delete(msg.data.id);
    if (msg.data.ok) p.resolve(msg.data.data);
    else p.reject(storageError(msg.data.error.name, msg.data.error.message));
  };
  // il worker è morto/mancante: le chiamate in coda NON restano sospese per sempre
  worker.onerror = () => {
    worker = null;
    rejectAllPending(storageError("HyprStorageUnavailable", "worker di storage non avviato"));
  };
  return worker;
}

function call<T>(payload: Record<string, unknown>): Promise<T> {
  const id = nextId++;
  return new Promise<T>((resolve, reject) => {
    pending.set(id, { resolve: resolve as (v: unknown) => void, reject });
    ensureWorker().postMessage({ id, ...payload });
  });
}

export function initDb(): Promise<{ mode: string }> {
  if (!storageAvailable()) {
    return Promise.reject(storageError("HyprStorageUnavailable", "Worker/WASM non disponibili su questo browser"));
  }
  initPromise ??= Promise.race([
    call<{ mode: string }>({ kind: "init" }),
    new Promise<never>((_, reject) => {
      setTimeout(() => reject(storageError("HyprStorageUnavailable", "init storage in timeout")), INIT_TIMEOUT_MS);
    }),
  ]).catch((err: Error) => {
    initPromise = null; // errore/timeout = tentativo ripetibile al prossimo tap, mai stato bloccato
    throw err;
  });
  return initPromise;
}

export async function appendEvents(events: HyprEvent[]): Promise<number> {
  await initDb();
  return call<number>({ kind: "append", events });
}

export async function listEvents(): Promise<HyprEvent[]> {
  await initDb();
  return call<HyprEvent[]>({ kind: "list" });
}
